import React, { useState } from "react";
import { Opportunity, FormatRule } from "../../types";
import { Modal } from "../common/Modal";
import {
  FileText,
  Plus,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Edit2,
  FileCheck,
  Type,
  Maximize2,
  PackageCheck,
  Loader2,
} from "lucide-react";

interface FormatRulesListProps {
  opportunity: Opportunity;
  rules: FormatRule[];
  onAddRule: () => void;
  onEditRule: (rule: FormatRule) => void;
  onDeleteRule: (ruleId: string) => Promise<void>;
  onToggleCompliance: (rule: FormatRule) => void;
  onToggleConfirmed: (rule: FormatRule) => void;
  onUpdateNotes: (ruleId: string, notes: string) => void;
}

export const FormatRulesList: React.FC<FormatRulesListProps> = ({
  opportunity: _opportunity,
  rules,
  onAddRule,
  onEditRule,
  onDeleteRule,
  onToggleCompliance,
  onToggleConfirmed,
  onUpdateNotes,
}) => {
  const [ruleToDelete, setRuleToDelete] = useState<FormatRule | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Groupings
  const pageRules = rules.filter((r) => r.ruleType === "page_limit" || r.ruleType === "margin");
  const typeRules = rules.filter(
    (r) => r.ruleType === "font" || (r.ruleType as string) === "spacing",
  );
  const packageRules = rules.filter(
    (r) =>
      r.ruleType === "file_format" ||
      r.ruleType === "file_size" ||
      r.ruleType === "submission_method" ||
      r.ruleType === "other",
  );

  const nonCompliantCount = rules.filter((r) => !r.isCompliant).length;

  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;
    setIsDeleting(true);
    try {
      await onDeleteRule(ruleToDelete.id);
      setRuleToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const renderRuleGroup = (
    title: string,
    description: string,
    icon: React.ComponentType<{ className?: string }>,
    items: FormatRule[],
  ) => {
    if (items.length === 0) return null;
    const Icon = icon;

    return (
      <div className="space-y-2.5">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#2ba5b5]" />
          <div>
            <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">{title}</h4>
            <span className="text-[11px] text-slate-500">{description}</span>
          </div>
        </div>

        <div className="space-y-2">
          {items.map((rule) => (
            <div
              key={rule.id}
              className={`glass rounded-xl border p-3.5 transition-all shadow-xs ${
                !rule.isCompliant
                  ? "border-rose-300 bg-rose-50/20 dark:border-rose-900/40 dark:bg-rose-950/10"
                  : rule.confirmed
                    ? "border-emerald-200/80 bg-emerald-50/20 dark:border-emerald-900/40 dark:bg-emerald-950/10"
                    : "border-slate-200/80 bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
              }`}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-700 uppercase dark:bg-slate-800 dark:text-slate-300">
                      {rule.ruleType.replace("_", " ")}
                    </span>
                    {rule.sourcePage !== undefined && (
                      <span className="text-[10px] text-slate-400">Page {rule.sourcePage}</span>
                    )}
                  </div>
                  <p className="text-xs font-medium text-slate-800 dark:text-slate-200">
                    {rule.requirement}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {/* Compliance Toggle */}
                  <button
                    type="button"
                    onClick={() => onToggleCompliance(rule)}
                    className={`btn-animate inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold ${
                      rule.isCompliant
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800"
                        : "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800"
                    }`}
                  >
                    {rule.isCompliant ? (
                      <>
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        <span>Compliant</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-3.5 w-3.5" />
                        <span>Non-Compliant</span>
                      </>
                    )}
                  </button>

                  {/* Confirmed Toggle */}
                  <button
                    type="button"
                    onClick={() => onToggleConfirmed(rule)}
                    className={`btn-animate inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ${
                      rule.confirmed
                        ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        : "border border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
                    }`}
                    title="Mark requirement reviewed & confirmed"
                  >
                    <FileCheck className="h-3.5 w-3.5" />
                  </button>

                  {/* Edit */}
                  <button
                    type="button"
                    onClick={() => onEditRule(rule)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                    title="Edit rule"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>

                  {/* Delete */}
                  <button
                    type="button"
                    onClick={() => setRuleToDelete(rule)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600 dark:hover:bg-slate-800 dark:hover:text-rose-400"
                    title="Delete rule"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Notes Input */}
              <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-800/80">
                <input
                  type="text"
                  placeholder="Verification note (e.g. Checked: 11pt Arial, 1-inch margins, max 25 pages)..."
                  defaultValue={rule.notes || ""}
                  onBlur={(e) => {
                    if (e.target.value !== (rule.notes || "")) {
                      onUpdateNotes(rule.id, e.target.value);
                    }
                  }}
                  className="w-full rounded-md border border-transparent px-2 py-1 text-[11px] text-slate-700 hover:border-slate-200 focus:border-[#2ba5b5] focus:bg-white focus:outline-hidden dark:text-slate-300 dark:hover:border-slate-700 dark:focus:bg-slate-950"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Alert banner if non-compliant rules exist */}
      {nonCompliantCount > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/80 p-3 text-xs text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300">
          <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
          <span>
            <strong>Formatting Notice:</strong> {nonCompliantCount} formatting constraint
            {nonCompliantCount === 1 ? " is" : "s are"} marked non-compliant. Ensure the proposal
            document strictly matches these rules before final package delivery.
          </span>
        </div>
      )}

      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
            Format & Submission Constraints
          </h3>
          <p className="text-xs text-slate-500">
            Enforce page limits, font rules, margins, and packaging instructions to avoid buyer
            disqualification.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddRule}
          className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238997]"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Add Format Rule</span>
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
          <FileText className="mx-auto h-8 w-8 text-slate-400" />
          <h4 className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
            No Format Rules Logged
          </h4>
          <p className="mt-1 text-xs text-slate-500">
            Extract constraints from the RFP or add page limits, font requirements, and packaging
            rules manually.
          </p>
          <button
            type="button"
            onClick={onAddRule}
            className="btn-animate mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add Format Rule</span>
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {renderRuleGroup(
            "Page & Margin Limits",
            "Maximum page caps, executive summary limits, and margin settings",
            Maximize2,
            pageRules,
          )}
          {renderRuleGroup(
            "Typography & Spacing",
            "Font families, minimum point sizes, line spacing, and header styles",
            Type,
            typeRules,
          )}
          {renderRuleGroup(
            "Packaging & Submission Delivery",
            "File format (PDF/Excel), maximum file size, electronic portal rules, and binders",
            PackageCheck,
            packageRules,
          )}
        </div>
      )}

      {/* Delete Rule Confirmation Modal */}
      <Modal
        isOpen={Boolean(ruleToDelete)}
        onClose={() => setRuleToDelete(null)}
        title="Confirm Rule Deletion"
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Are you sure you want to delete this format rule?
          </p>
          <p className="rounded-lg bg-slate-50 p-2 text-xs font-medium text-slate-800 dark:bg-slate-950 dark:text-slate-200">
            &ldquo;{ruleToDelete?.requirement}&rdquo;
          </p>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setRuleToDelete(null)}
              className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isDeleting}
              onClick={handleConfirmDelete}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-rose-700 disabled:opacity-50"
            >
              {isDeleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              <span>Delete Rule</span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
